/* A local copy of the commerce attribute catalog proxima-audiences validates against; the service has no read endpoint for it.
   Source: `dbt/seeds/brand_tag_commerce_attribute_persona_mapping.csv` in proxima.science.dbt, which seeds
   PROXIMA_CONSUMER_GRAPH.STAGING.BRAND_TAG_COMMERCE_ATTRIBUTE_PERSONA_MAPPING. Regenerate this file when the seed changes.
   A key is the seed's lowercase `field_name`, and the service accepts it only while it is a boolean column on CONSUMER_ATTRIBUTES_PIVOTED.
   The seed's brand tag columns are left out: they are free text under revision, and nothing in Nova reads them. */
export type CommerceAttributeOption = {
  key: string;
  label: string;
  /** The seed's `section`, a topic heading such as `PETS`. */
  section: string;
};

/* Section, then its keys, in seed order */
const CATALOG: Record<string, string[]> = {
  'BEAUTY & PERSONAL CARE': [
    'lifestyle_bold_experimental_beauty',
    'lifestyle_clean_beauty',
    'lifestyle_fragrance_collector',
    'lifestyle_mens_grooming',
    'lifestyle_self_care_ritual',
    'lifestyle_skincare_first'
  ],
  'DIET & EATING': [
    'lifestyle_alcohol_free',
    'lifestyle_beverage_entertainer',
    'lifestyle_gluten_free',
    'lifestyle_gourmet_home_cooking',
    'lifestyle_high_protein',
    'lifestyle_home_coffee_ritual',
    'lifestyle_indulgent_treats',
    'lifestyle_keto_lowcarb',
    'lifestyle_organic_clean_label',
    'lifestyle_vegan_plantbased'
  ],
  'FASHION & STYLE': [
    'lifestyle_alt_subculture',
    'lifestyle_athleisure_everyday',
    'lifestyle_classic_preppy',
    'lifestyle_minimalist_luxe',
    'lifestyle_streetwear_adjacent',
    'lifestyle_sustainable_fashion',
    'lifestyle_trend_forward',
    'lifestyle_workleisure'
  ],
  'FITNESS & MOVEMENT': [
    'lifestyle_combat_outdoor_sport',
    'lifestyle_cycling',
    'lifestyle_golf',
    'lifestyle_hiking_camping',
    'lifestyle_premium_activewear',
    'lifestyle_running_endurance',
    'lifestyle_snow_sports',
    'lifestyle_strength_training',
    'lifestyle_water_surf_sports',
    'lifestyle_yoga_studio'
  ],
  'HOME & LIVING': [
    'lifestyle_clean_sustainable_home',
    'lifestyle_cozy_nesting',
    'lifestyle_home_entertaining',
    'lifestyle_interior_design',
    'lifestyle_minimalist_home',
    'lifestyle_plant_parent'
  ],
  'PARENTING & FAMILY': [
    'lifestyle_family_outdoor',
    'lifestyle_new_parent_0_2',
    'lifestyle_premium_parenting',
    'lifestyle_school_age_parent',
    'lifestyle_toddler_parent'
  ],
  PETS: ['lifestyle_cat_owner', 'lifestyle_dog_owner'],
  'SPORTS & FANDOM': ['lifestyle_sport_spectator', 'lifestyle_team_sport_player'],
  'TRAVEL & ADVENTURE': ['lifestyle_adventure_traveler', 'lifestyle_frequent_traveler'],
  'VALUES & ORIENTATION': [
    'lifestyle_alternative_wellness',
    'lifestyle_creative_maker',
    'lifestyle_cultural_identity',
    'lifestyle_eco_conscious',
    'lifestyle_faith_identity',
    'lifestyle_gaming_pop_culture',
    'lifestyle_heritage_craft',
    'lifestyle_social_values_buyer',
    'lifestyle_tech_early_adopter',
    'lifestyle_thoughtful_gifter'
  ],
  'WELLNESS & MINDFULNESS': [
    'lifestyle_biohacking',
    'lifestyle_functional_nutrition',
    'lifestyle_holistic_wellness',
    'lifestyle_mindfulness_meditation',
    'lifestyle_sleep_optimization',
    'lifestyle_targeted_health',
    'lifestyle_womens_health'
  ]
};

/* "lifestyle_combat_outdoor_sport" -> "Combat outdoor sport": the `lifestyle_` prefix is on every key, so it is dropped */
export const humanizeAttributeKey = (key: string): string => {
  const [, ...rest] = key.split('_');
  const words = (rest.length > 0 ? rest : [key]).join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/* "FITNESS & MOVEMENT" -> "Fitness & Movement" */
export const sectionLabel = (section: string): string =>
  section
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const COMMERCE_ATTRIBUTE_OPTIONS: readonly CommerceAttributeOption[] = Object.entries(
  CATALOG
).flatMap(([section, keys]) =>
  keys.map(key => ({ key, label: humanizeAttributeKey(key), section }))
);

/* Every key renders humanized, inside the local catalog or not */
export const commerceAttributeLabel = (key: string): string => humanizeAttributeKey(key);
